import { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import "../../Styles/style.css";
import {
  Alert,
  Box,
  InputAdornment,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import { BASE_URL } from "../../components/config/config";
import axios from "axios";
import ViewActions from "../../components/AdminApi/ViewActions";
import DeleteAlertDialogModal from "../../components/Components/DeleteAlert";
import AdminApiModule from "../../components/AdminApi/Api/api";
import {
  canViewTechnicians,
  checkPermission,
} from "../../utility/permissionUtils";
import TechnicianFilters from "./Filters/TechnicianFilters";

const Technicians = () => {
  const [columns, setColumns] = useState([]);
  const [technicianList, setTechnicianList] = useState([]);
  const [isAuthorizedForPopupOpen, setIsAuthorizedForPopupOpen] =
    useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  const [isDeleteAlertPopupOpen, setIsDeleteAlertPopupOpen] = useState(false);
  const [deleteTechnicianId, setDeleteTechnicianId] = useState("");
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const navigate = useNavigate();
  const reduxState = useSelector((state) => state.userReducer);
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  // Check permissions
  const hasFullAccess = checkPermission(
    userPermissions,
    "technician_details.full_access"
  );
  const canViewTechnician =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.view_technician"
    ) || hasFullAccess;
  const canViewDocument =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.view_document"
    ) || hasFullAccess;
  const canAuthorizeClient =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.authorize_client"
    ) || hasFullAccess;
  const canViewNumber =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.view_number"
    ) || hasFullAccess;
  const canDeleteTechnician =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.delete_technician"
    ) || hasFullAccess;
  const canDownloadList =
    checkPermission(
      userPermissions,
      "technician_details.update_technician_details.download_technician_list"
    ) || hasFullAccess;

  const options = {
    filterType: "dropdown",
    download: canDownloadList,
    print: canDownloadList,
  };

  // Initialize columns based on permissions
  useEffect(() => {
    const baseColumns = [
      {
        name: "name",
        label: "Name",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "lastSeen",
        label: "Last Seen",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "techId",
        label: "Technician ID",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "pendingTickets",
        label: "Pending Requests",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "closedTickets",
        label: "Closed Requests",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "TotalTickets",
        label: "Total Requests",
        options: {
          filter: true,
          sort: true,
          customFilterListOptions: {
            render: (v) => {
              if (v === "0") return "0";
              if (v === "1") return "1";
              if (v === "2") return "2";
              if (v === "3") return "3";
              if (v === "4") return "4";
              if (v === "5") return "5";
              if (v === "6") return "6";
              if (v === "7") return "7";
              if (v === "8") return "8";
              if (v === "9") return "9";
              if (v === "10") return "10";
              if (v === "GreaterThan10") return "Greater Than 10";
              return true;
            },
          },
          filterOptions: {
            names: [
              "0",
              "1",
              "2",
              "3",
              "4",
              "5",
              "6",
              "7",
              "8",
              "9",
              "10",
              "GreaterThan10",
            ],
            logic: (value, filters) => {
              const numericValue = parseFloat(value);

              if (filters.includes("0") && numericValue != 0) return true;
              if (filters.includes("1") && numericValue != 1) return true;
              if (filters.includes("2") && numericValue != 2) return true;
              if (filters.includes("3") && numericValue != 3) return true;
              if (filters.includes("4") && numericValue != 4) return true;
              if (filters.includes("5") && numericValue != 5) return true;
              if (filters.includes("6") && numericValue != 6) return true;
              if (filters.includes("7") && numericValue != 7) return true;
              if (filters.includes("8") && numericValue != 8) return true;
              if (filters.includes("9") && numericValue != 9) return true;
              if (filters.includes("10") && numericValue != 10) return true;
              if (filters.includes("GreaterThan10") && numericValue < 10)
                return true;
              return false;
            },
          },
        },
      },
      {
        name: "pincode",
        label: "Service Pincode",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "email",
        label: "Email",
        options: {
          filter: true,
          sort: false,
        },
      },
    ];

    // Add phone column only if user has view number permission
    if (canViewNumber) {
      baseColumns.push({
        name: "phone",
        label: "Phone",
        options: {
          filter: true,
          sort: false,
        },
      });
    }

    baseColumns.push(
      {
        name: "Rating",
        label: "Rating",
        options: {
          customBodyRender: (value) => {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {value} <StarIcon style={{ color: "yellow" }} />
              </div>
            );
          },
          filter: true,
          sort: false,
        },
      },
      {
        name: "profile_created_by",
        label: "Designation",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "referenceDetails",
        label: "Reference Details",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "address_details_permanent",
        label: "Address Details Permanent",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "associatedWith",
        label: "Associated CenterCode",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "authorizedFor",
        label: "Authorized For Clients",
        options: {
          filter: true,
          sort: false,
        },
      }
    );

    // Add document view column only if user has view document permission
    if (canViewDocument) {
      baseColumns.push({
        name: "action",
        label: "View Verification Details",
        options: {
          customBodyRender: (value) => {
            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto auto auto auto",
                  gridGap: "-10px !important",
                }}
              >
                {value &&
                  value.document_details &&
                  value.document_details.aadhar_card_document &&
                  value.document_details.aadhar_card_document.front_side &&
                  value.document_details.aadhar_card_document.front_side.endsWith(
                    ".jpg"
                  ) && (
                    <Button
                      className="text-transform-none"
                      variant="contained"
                      onClick={() => handleClick(value._id)}
                    >
                      Views
                    </Button>
                  )}
              </div>
            );
          },
        },
      });
    }

    // Add action column with conditional buttons based on permissions
    baseColumns.push({
      name: "action",
      label: "Action",
      options: {
        customBodyRender: (value) => {
          return (
            <>
              {
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width={
                    canAuthorizeClient && canDeleteTechnician ? "210px" : "auto"
                  }
                >
                  {canAuthorizeClient && (
                    <Button
                      className="text-transform-none"
                      variant="contained"
                      onClick={() => toggleAuthorizedForPopup(value._id)}
                    >
                      Authorized For
                    </Button>
                  )}

                  {canDeleteTechnician && (
                    <Tooltip title="Delete">
                      <Button
                        style={{ background: "#FCE9F1" }}
                        onClick={() => clickDeleteTicket(value?._id)}
                      >
                        <DeleteIcon style={{ color: "red" }} />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              }
            </>
          );
        },
      },
    });

    setColumns(baseColumns);
  }, [userPermissions]);

  const handleClick = (technicianId) => {
    navigate(`/tecnicianDocument?technicianId=${technicianId}`);
  };

  const refreshData = () => {
    // Clear applied filters when refreshing
    setAppliedFilters({});
    localStorage.removeItem("technicianFilters");
    localStorage.removeItem("technicianDateRange");
    localStorage.removeItem("technicianActiveFilters");

    getAllTechnicians();
  };

  const handleFilterChange = (filters) => {
    setAppliedFilters(filters);
    getAllTechniciansWithFilters(filters);
  };

  const getAllTechniciansWithFilters = (filters = {}) => {
    // Remove empty filters
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) => value !== "" && value != null
      )
    );

    new AdminApiModule()
      .getAllTechnician(cleanFilters)
      .then((res) => {
        const mappedTechnician = res?.data?.map((ite) => {
          const pendingTickets = ite?.count_details?.pending_ticket_count || 0;
          const closedTickets = ite?.count_details?.closed_ticket_count || 0;
          const totalTickets = pendingTickets + closedTickets;

          return {
            techId: ite?.technician_id || "-",
            name: ite?.personal_details?.name,
            email: `${ite?.personal_details?.email?.slice(0, 10)}...`,
            phone:
              ite?.personal_details?.phone?.country_code +
              ite?.personal_details?.phone?.mobile_number,
            Rating: ite?.count_details?.technician_rating || 0,
            profile_created_by:
              ite?.profile_created_by === "SELF_BY_APP"
                ? "CENTER"
                : ite?.profile_created_by === "CENTER"
                ? "TECHNICIAN"
                : ite?.profile_created_by,
            referenceDetails: ite?.referenceDetails?.reference_person_name,
            address_details_permanent:
              ite?.address_details_permanent?.address_line?.slice(0, 8),
            associatedWith: getAssociatedCenters(ite?.center_id),
            authorizedFor: getFormattedClients(ite?.clients_ids_list),
            personal: ite?.personal_details?.profile_picture,
            Aadharback: ite?.document_details?.aadhar_card_document?.back_side,
            Aadharfront:
              ite?.document_details?.aadhar_card_document?.front_side,
            action: ite,
            pendingTickets,
            closedTickets,
            TotalTickets: totalTickets,
            imgUrl: ite,
            pincode: ite?.service_area_main_pincode,
            lastSeen: ite?.lastSeen || "-",
          };
        });
        setTechnicianList(mappedTechnician);
      })
      .catch(console.error);
  };

  const onClickFilter = (value) => {
    const payload = {
      start_date: value[0],
      end_date: value[1],
    };

    new AdminApiModule()
      .getAllTechnician(payload)
      .then((res) => {
        const mappedTechnician = res?.data?.map((ite) => ({
          name: ite?.personal_details?.name,
          phone:
            ite?.personal_details?.phone?.country_code +
            ite?.personal_details?.phone?.mobile_number,
          profile_created_by: ite?.profile_created_by,
          referenceDetails: ite?.referenceDetails?.reference_person_name,
          address_details_permanent:
            ite?.address_details_permanent?.address_line +
            "  " +
            ite?.address_details_permanent?.city +
            " " +
            ite?.address_details_permanent?.country,
          associatedWith: getAssociatedCenters(ite?.center_id),
          authorizedFor: getFormattedClients(ite?.clients_ids_list),
          pincode: ite?.service_area_main_pincode,
          action: ite,
        }));
        setTechnicianList(mappedTechnician);
      })
      .catch(console.error);
  };

  const showDialog = (open, type, msg) => {
    setToast({
      open,
      msg,
      duration: 5000,
      type,
    });
  };

  const closeDialog = () => {
    setToast({
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const getAllClients = () => {
    new AdminApiModule()
      .getAllClients()
      .then((res) => {
        const parsedData =
          res && res.data && res.data.length > 0 ? res.data : [];
        setClientsList(parsedData);
      })
      .catch(console.error);
  };

  const getFormattedClients = (clients) => {
    const clients_list = [];
    clients?.forEach((item) => {
      clients_list.push(item.client_name);
    });
    return clients_list.length > 0 ? clients_list.join(", ") : "";
  };

  const getAllTechnicians = () => {
    new AdminApiModule()
      .getAllTechnician()
      .then((res) => {
        const mappedTechnician = res?.data?.map((ite) => {
          const pendingTickets = ite?.count_details?.pending_ticket_count || 0;
          const closedTickets = ite?.count_details?.closed_ticket_count || 0;
          const totalTickets = pendingTickets + closedTickets;

          return {
            techId: ite?.technician_id || "-",
            name: ite?.personal_details?.name,
            email: `${ite?.personal_details?.email?.slice(0, 10)}...`,
            phone:
              ite?.personal_details?.phone?.country_code +
              ite?.personal_details?.phone?.mobile_number,
            Rating: ite?.count_details?.technician_rating || 0,
            profile_created_by:
              ite?.profile_created_by === "SELF_BY_APP"
                ? "CENTER"
                : ite?.profile_created_by === "CENTER"
                ? "TECHNICIAN"
                : ite?.profile_created_by,
            referenceDetails: ite?.referenceDetails?.reference_person_name,
            address_details_permanent:
              ite?.address_details_permanent?.address_line?.slice(0, 8),
            associatedWith: getAssociatedCenters(ite?.center_id),
            authorizedFor: getFormattedClients(ite?.clients_ids_list),
            personal: ite?.personal_details?.profile_picture,
            Aadharback: ite?.document_details?.aadhar_card_document?.back_side,
            Aadharfront:
              ite?.document_details?.aadhar_card_document?.front_side,
            action: ite,
            pendingTickets,
            closedTickets,
            TotalTickets: totalTickets,
            imgUrl: ite,
            pincode: ite?.service_area_main_pincode,
            lastSeen: ite?.lastSeen || "-",
          };
        });
        setTechnicianList(mappedTechnician);
      })
      .catch(console.error);
  };

  const toggleAuthorizedForPopup = (technicianId = "") => {
    if (technicianId) {
      setSelectedTechnicianId(technicianId);
    }
    setIsAuthorizedForPopupOpen(!isAuthorizedForPopupOpen);
  };

  const clickDeleteTicket = (ticketId) => {
    if (!canDeleteTechnician) {
      showDialog(
        true,
        "error",
        "You don't have permission to delete technicians"
      );
      return;
    }
    setDeleteTechnicianId(ticketId);
    setIsDeleteAlertPopupOpen(true);
    localStorage.setItem("technicianId", ticketId);
  };

  const handleDeleteCloseModal = () => {
    setIsDeleteAlertPopupOpen(false);
  };

  const handleDeleteRows = async () => {
    if (!canDeleteTechnician) {
      showDialog(
        true,
        "error",
        "You don't have permission to delete technicians"
      );
      return;
    }

    const accessToken = localStorage.getItem("access_token");
    const technician_Id = localStorage.getItem("technicianId");

    try {
      await axios.delete(`${BASE_URL}admin/deleteTechnician/${technician_Id}`, {
        headers: {
          "x-access-token": accessToken,
        },
      });
      handleDeleteCloseModal();
      getAllTechnicians();
      showDialog(true, "success", "Technician deleted successfully");
    } catch (error) {
      console.error(error);
      showDialog(true, "error", "Failed to delete technician");
    }
  };

  const getAssociatedCenters = (centersList) => {
    const centers_list = [];
    centersList?.forEach((item) => {
      centers_list.push(item?.qr_details?.qr_id);
    });
    return centers_list.length > 0 ? centers_list.join(", ") : "";
  };

  const saveAuthorizedTechnicians = (techniciansSelected) => {
    if (!canAuthorizeClient) {
      showDialog(
        true,
        "error",
        "You don't have permission to authorize clients"
      );
      return;
    }

    const payload = {
      technician_obj_id: selectedTechnicianId,
      clients_ids_list: [...techniciansSelected],
    };

    new AdminApiModule()
      .adminUpdateTechnician(payload)
      .then(() => {
        showDialog(true, "success", "Technician updated successfully");
        setTimeout(() => {
          toggleAuthorizedForPopup();
          getAllTechnicians();
        }, 1000);
      })
      .catch((err) => {
        console.error(err);
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  // Load saved filters and apply them on component mount
  const loadSavedFilters = () => {
    const savedFilters = localStorage.getItem("technicianFilters");
    const savedDateRange = localStorage.getItem("technicianDateRange");

    if (savedFilters || savedDateRange) {
      const filters = savedFilters ? JSON.parse(savedFilters) : {};
      const dateRange = savedDateRange ? JSON.parse(savedDateRange) : {};

      const allFilters = { ...filters, ...dateRange };
      setAppliedFilters(allFilters);

      getAllTechniciansWithFilters(allFilters);
    } else {
      getAllTechnicians();
    }
  };

  useEffect(() => {
    if (canViewTechnician) {
      loadSavedFilters();
      getAllClients();
    }
  }, [canViewTechnician]);

  // Check if user has permission to view technicians at all
  if (!canViewTechnicians(userPermissions)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1">
          You don't have permission to view technicians. Please contact your
          administrator.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {toast.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={toast.open}
          autoHideDuration={toast.duration}
          onClose={closeDialog}
        >
          <Alert
            onClose={closeDialog}
            severity={toast.type}
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        </Snackbar>
      )}

      <TechnicianFilters
        onFilterChange={handleFilterChange}
        clientsList={clientsList}
        refreshData={refreshData}
      />

      <MUIDataTable
        title={"Technicians"}
        data={technicianList}
        columns={columns}
        options={options}
      />

      {isDeleteAlertPopupOpen && (
        <DeleteAlertDialogModal
          openAlert={isDeleteAlertPopupOpen}
          handleAlertClose={handleDeleteCloseModal}
          handleDeleteTicket={handleDeleteRows}
          Heading="Technician"
        />
      )}

      {canAuthorizeClient && (
        <ViewActions
          isAuthorizedForPopupOpen={isAuthorizedForPopupOpen}
          clientsList={clientsList}
          toggleAuthorizedForPopup={toggleAuthorizedForPopup}
          saveAuthorizedCenters={saveAuthorizedTechnicians}
        />
      )}
    </div>
  );
};

export default Technicians;
